import React, { useState, useEffect } from 'react';
import { analyticsAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';
import ConfirmModal from '../../components/common/ConfirmModal';
import Toast from '../../components/common/Toast';

const ActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState('all');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => {
    fetchActivityLogs();
  }, [currentPage, filter]);

  const fetchActivityLogs = async () => {
    setLoading(true);
    try {
      const response = await analyticsAPI.getActivityLog({
        action_type: filter === 'all' ? '' : filter,
        page: currentPage,
        limit: 20,
      });

      if (response.data.success) {
        setLogs(response.data.data.logs);
        setTotalPages(response.data.data.pagination.total_pages);
      }
    } catch (error) {
      setToast({ show: true, message: 'Failed to fetch activity logs', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Add clear handler
  const handleClearLogs = async () => {
    try {
      await analyticsAPI.clearActivityLog();
      setToast({ show: true, message: 'Activity log cleared successfully', type: 'success' });
      fetchActivityLogs();
      setConfirmClear(false);
    } catch (error) {
      setToast({ show: true, message: error.response?.data?.message || 'Failed to clear logs', type: 'error' });
    }
  };

  const getActionBadge = (action) => {
    const badges = {
      create: 'badge-success',
      update: 'badge-info',
      delete: 'badge-danger',
      login: 'badge-primary',
      logout: 'badge-secondary',
    };
    return badges[action] || 'badge-secondary';
  };

  return (
    <div>
      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />

      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="page-title">Activity Log</h2>
            <p className="page-subtitle">Admin actions and system events</p>
          </div>
          {logs.length > 0 && (
            <button
              className="btn btn-outline-danger"
              onClick={() => setConfirmClear(true)}
            >
              <i className="bi bi-trash me-2"></i>Clear Logs
            </button>
          )}
        </div>
      </div>

      <div className="stat-card mb-4">
        <div className="row align-items-center">
          <div className="col-md-6">
            <select className="form-select" value={filter} onChange={(e) => { setFilter(e.target.value); setCurrentPage(1); }}>
              <option value="all">All Actions</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="login">Login</option>
              <option value="logout">Logout</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="data-table">
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Admin</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-4">
                      <i className="bi bi-inbox fs-1 text-muted d-block mb-2"></i>
                      <p className="text-muted mb-0">No activity logs found</p>
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id}>
                      <td><small>{new Date(log.created_at).toLocaleString()}</small></td>
                      <td>{log.admin_name}</td>
                      <td><span className={`badge ${getActionBadge(log.action_type)}`}>{log.action_type}</span></td>
                      <td>{log.resource_type}</td>
                      <td><small className="text-muted">{log.details || 'N/A'}</small></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <ConfirmModal
  show={confirmClear}
  onClose={() => setConfirmClear(false)}
  onConfirm={handleClearLogs}
  title="Clear Activity Log"
  message="Are you sure you want to clear all activity logs? This action cannot be undone."
  type="danger"
/>
          </div>
          {totalPages > 1 && (
            <div className="p-3 border-top">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActivityLog;